/* COD/Bank Transfer payment-method add-on for the REAL /products/pillow buy-box
   (product.pillow-cod-test template only -- see the "COD box" custom-liquid block in that
   template). Deliberately additive: the existing quantity-tier picker (the 3rd-party
   ".omg-offer" widget) and the native Shopify product-form are left completely alone.

   How it plugs in:
   - The tier widget already keeps the native product-form's hidden `id` (variant) and
     `quantity` inputs in sync with whichever tier the customer has selected, and (crucially)
     the real quantity-break pricing for that variant+quantity is whatever Shopify actually
     applies at checkout -- so we read those two hidden inputs at submit time rather than
     re-implementing tier pricing here.
   - If the customer never touches payment method, the native "Add to cart" button behaves
     exactly as it does today (this script never intercepts that path).
   - If the customer picks Cash on Delivery / Bank Transfer, submitting the form is redirected
     into the same session-based, cart-isolated funnel already built and tested for the
     isolated COD experiment (sessionStorage record -> pages/cod-order-confirmation, which
     calls the existing Cloudflare Worker to create the real order with real Storefront-priced
     totals). No real Shopify cart is ever touched by that path either.

   Now includes the same post-purchase upsell hop as the isolated test funnel: after a
   successful PDP submit, the customer is sent to pages/cod-order-upsell first, and only
   reaches pages/cod-order-confirmation from there (either by accepting or declining the
   upsell) -- see sections/page-cod-upsell.liquid + assets/pdp-cod-pages.js. */
(function () {
  'use strict';

  var PCO_SESSION_KEY = 'pco_session_v1';
  var CONFIRM_PATH = 'pages/cod-order-confirmation';
  // Per-product settings come from window.PCO_REAL_PDP_CONFIG (set in the product
  // template's COD box). Every field is optional; the defaults below are exactly the
  // original pillow behaviour, so the pillow template needs no config changes.
  var DEFAULT_UPSELL_PATH = 'pages/cod-order-upsell';
  var PAYMENT_METHOD_LABELS = { cod: 'Cash on Delivery', bank: 'Bank Transfer' };

  function pcoGenToken() {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    return 'pco-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);
  }

  function pcoSaveSession(session) {
    try { sessionStorage.setItem(PCO_SESSION_KEY, JSON.stringify(session)); } catch (e) { /* noop */ }
  }

  function formatThaiPhone(raw) {
    var digits = (raw || '').replace(/\D/g, '');
    if (digits.indexOf('66') === 0 && digits.length > 9) { digits = digits.slice(2); }
    digits = digits.replace(/^0+/, '');
    return digits ? '+66' + digits : '';
  }

  function root() { return (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/'); }

  function buildPaymentBlockHtml() {
    return ''
      + '<div class="pco-real-payment" id="pco-real-payment">'
      + '  <p class="pco-real-label">เลือกวิธีการชำระเงิน</p>'
      + '  <div class="pco-real-methods">'
      + '    <button type="button" class="pco-real-method-btn" data-method="cod">เก็บเงินปลายทาง</button>'
      + '    <button type="button" class="pco-real-method-btn" data-method="bank">โอนเงินผ่านธนาคาร</button>'
      + '  </div>'
      + '  <div class="pco-real-total" id="pco-real-total" style="display:none;justify-content:space-between;align-items:center;gap:12px;padding:14px 0;margin:16px 0 6px;border-top:1px dashed #d5d5d5;border-bottom:1px dashed #d5d5d5;">'
      + '    <span id="pco-real-total-label" style="font-weight:700;font-size:16px;color:#111;text-align:left;flex:1 1 auto;min-width:0;"></span>'
      + '    <span id="pco-real-total-value" style="white-space:nowrap;text-align:right;flex:0 0 auto;"></span>'
      + '  </div>'
      + '  <div class="pco-real-form" id="pco-real-form" style="display:none;">'
      + '    <div class="pco-real-field"><label for="pco-real-name">ชื่อ-สกุล</label><input type="text" id="pco-real-name" placeholder="ชื่อ-สกุล" autocomplete="name"></div>'
      + '    <div class="pco-real-field"><label for="pco-real-phone">เบอร์โทร</label><div class="pco-real-phone-wrap"><span class="pco-real-phone-flag">🇹🇭 +66</span><input type="tel" id="pco-real-phone" placeholder="8X XXX XXXX" autocomplete="tel" inputmode="numeric"></div></div>'
      + '    <div class="pco-real-field"><label for="pco-real-email">อีเมล</label><input type="email" id="pco-real-email" placeholder="อีเมล" autocomplete="email"></div>'
      + '    <div class="pco-real-field"><label for="pco-real-address">ที่อยู่ (โปรดกรอกให้ครบถ้วน)</label><input type="text" id="pco-real-address" placeholder="ที่อยู่ (บ้านเลขที่ ถนน ตำบล/แขวง)" autocomplete="street-address"></div>'
      + '    <div class="pco-real-field"><label for="pco-real-zip">รหัสไปรษณีย์</label><input type="text" id="pco-real-zip" placeholder="รหัสไปรษณีย์" autocomplete="postal-code" inputmode="numeric"></div>'
      + '  </div>'
      + '  <p class="pco-real-note" id="pco-real-note" style="display:none;"></p>'
      + '  <p class="pco-real-card-link"><a href="javascript:void(0)" id="pco-real-card-link">ต้องการชำระด้วยบัตรเดบิต/เครดิต หรือช่องทางอื่น? คลิกที่นี่</a></p>'
      + '  <p class="pco-real-msg" id="pco-real-msg" hidden></p>'
      + '</div>';
  }

  // Hotlinked from the existing live theme asset -- same icon already used on the reference
  // "2.0" buy button, no need to re-upload it as our own asset.
  var CART_ICON_URL = 'https://ergolab.co/cdn/shop/t/25/assets/pdp-cart-btn-icon.webp';

  // Free "หน้ากากตา" (sleeping/eye mask) gift, 1 per pillow -- see the matching constant
  // and comment in pdp-cod-pages.js. Included here too so a customer who abandons at the
  // upsell step and gets auto-finalized 15 min later (see the Worker's finalizePendingOrders)
  // still gets the correct front-end-only mask quantity on their order.
  var FREE_MASK_VARIANT_ID = '42610235867241';

  function escapeHtml(str) {
    return String(str == null ? '' : str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  function formatBaht(n) {
    return '฿' + Math.round(Number(n) || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // Optional front-end cross-sell rows (e.g. the ARC back cushion on the MAX seat page).
  // Rendered by us (not the theme's async "complementary products" block) so the COD
  // order can carry them. Pricing is NOT trusted from here: the Worker prices every
  // non-upsell line through the live Storefront cart, exactly like the main product.
  function buildAddonsHtml(addons) {
    if (!addons || !addons.length) return '';
    var html = '<div class="pco-real-addons" id="pco-real-addons">';
    addons.forEach(function (a, i) {
      var price = Number(a.price) || 0;
      var compare = Number(a.compare) || 0;
      html += '<div class="pco-real-addon" data-index="' + i + '" role="group">'
        + '<label class="pco-real-addon-checkwrap"><input type="checkbox" class="pco-real-addon-check"><span class="pco-real-addon-box" aria-hidden="true"></span></label>'
        + (a.image ? '<img class="pco-real-addon-img" src="' + escapeHtml(a.image) + '" alt="" loading="lazy">' : '')
        + '<span class="pco-real-addon-info">'
        + '<span class="pco-real-addon-title">' + escapeHtml(a.title) + '</span>'
        + (a.subtitle ? '<span class="pco-real-addon-sub">' + escapeHtml(a.subtitle) + '</span>' : '')
        + '<span class="pco-real-addon-price">'
        + (compare > price ? '<s>' + formatBaht(compare) + '</s> ' : '')
        + '<b>' + formatBaht(price) + '</b></span>'
        + '</span>'
        + '<span class="pco-real-addon-qty" aria-label="จำนวน">'
        + '<button type="button" class="pco-real-addon-qbtn pco-real-addon-minus" aria-label="ลดจำนวน">&minus;</button>'
        + '<span class="pco-real-addon-qval">1</span>'
        + '<button type="button" class="pco-real-addon-qbtn pco-real-addon-plus" aria-label="เพิ่มจำนวน">+</button>'
        + '</span>'
        + '</div>';
    });
    return html + '</div>';
  }

  // Order bump (25 Oct 2026, pillow pages): one ClickFunnels-style checkbox box right above the
  // buy button. COD / bank: appears once the delivery form is filled in (the customer has
  // committed). Card / other payment: appears as soon as that option is picked. Quantity is
  // 1 per pillow in the selected bundle. Goes into the order as a normal add-on line.
  var BUMP_CSS = ''
    + '.pco-bump{margin:16px 0 4px;border:2px dashed #E2483D;border-radius:10px;background:#FFFDF3;overflow:hidden;animation:pcoBumpIn .35s ease-out}'
    + '@keyframes pcoBumpIn{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:none}}'
    + '.pco-bump-head{display:flex;align-items:center;gap:10px;padding:11px 12px;background:#FFF0B3;cursor:pointer;user-select:none;-webkit-tap-highlight-color:transparent}'
    + '.pco-bump-arrow{color:#E2483D;font-size:20px;line-height:1;animation:pcoBumpNudge 1s ease-in-out infinite}'
    + '@keyframes pcoBumpNudge{0%,100%{transform:translateX(0)}50%{transform:translateX(5px)}}'
    + '.pco-bump-box{flex:0 0 22px;width:22px;height:22px;border:2px solid #1A256C;border-radius:5px;background:#fff;display:flex;align-items:center;justify-content:center}'
    + '.pco-bump-box svg{width:14px;height:14px;opacity:0}'
    + '.pco-bump-headtxt{font-weight:800;font-size:15px;line-height:1.3;color:#111}'
    + '.pco-bump-body{display:flex;gap:12px;align-items:flex-start;padding:10px 12px 12px;cursor:pointer}'
    + '.pco-bump-img{flex:0 0 48px;width:48px;height:66px;object-fit:contain}'
    + '.pco-bump-txt{font-size:13.5px;line-height:1.5;color:#333;text-align:left}'
    + '.pco-bump-txt .pco-bump-offer{color:#E2483D;font-weight:800}'
    + '.pco-bump-price{display:block;margin-top:4px;font-size:14px;color:#111}'
    + '.pco-bump-price s{color:#888;margin-left:4px;font-size:13px}'
    + '.pco-bump-price b{color:#1A256C;font-weight:800;font-size:16px}'
    + '.pco-bump-qty{display:block;color:#666;font-size:12.5px}'
    + '.pco-bump.on{border-style:solid;border-color:#1A8F3C;background:#F4FBF6}'
    + '.pco-bump.on .pco-bump-head{background:#DDF3E3}'
    + '.pco-bump.on .pco-bump-arrow{visibility:hidden;animation:none}'
    + '.pco-bump.on .pco-bump-box{background:#1A8F3C;border-color:#1A8F3C}'
    + '.pco-bump.on .pco-bump-box svg{opacity:1}'
    // V2 (split test 25 Oct 2026): calm product card with an on/off switch, no ClickFunnels styling.
    + '.pco-bump--v2{border:1.5px solid #D9E1F2;border-radius:14px;background:#fff;box-shadow:0 2px 10px rgba(26,37,108,.06);padding:12px 12px 12px 10px;cursor:pointer;-webkit-tap-highlight-color:transparent}'
    + '.pco-b2-label{font-size:12px;font-weight:700;letter-spacing:.3px;color:#1A256C;margin:0 0 8px 2px;text-align:left}'
    + '.pco-b2-row{display:flex;align-items:center;gap:12px}'
    + '.pco-b2-img{flex:0 0 52px;width:52px;height:64px;object-fit:contain;background:#EEF3FB;border-radius:10px;padding:4px}'
    + '.pco-b2-info{flex:1 1 auto;min-width:0;text-align:left}'
    + '.pco-b2-title{font-weight:800;font-size:14.5px;line-height:1.3;color:#111}'
    + '.pco-b2-badge{display:inline-block;margin-left:6px;padding:2px 7px;border-radius:20px;background:#E8F6EC;color:#1A8F3C;font-size:11px;font-weight:800;vertical-align:1px}'
    + '.pco-b2-sub{font-size:12.5px;color:#666;line-height:1.4;margin-top:2px}'
    + '.pco-bump--v2 .pco-bump-price{margin-top:4px;font-size:13px;color:#333}'
    + '.pco-bump--v2 .pco-bump-price b{font-size:15px}'
    + '.pco-b2-switch{flex:0 0 46px;width:46px;height:28px;border-radius:28px;background:#D3D8E3;position:relative;transition:background .2s}'
    + '.pco-b2-switch i{position:absolute;top:3px;left:3px;width:22px;height:22px;border-radius:50%;background:#fff;box-shadow:0 1px 3px rgba(0,0,0,.25);transition:left .2s}'
    + '.pco-bump--v2.on{border-color:#1A256C;background:#F4F7FF}'
    + '.pco-bump--v2.on .pco-b2-switch{background:#1A256C}'
    + '.pco-bump--v2.on .pco-b2-switch i{left:21px}';

  function buildBumpHtml(b) {
    return ''
      + '<div class="pco-bump" id="pco-bump" role="checkbox" aria-checked="false" tabindex="0" style="display:none;">'
      + '  <div class="pco-bump-head">'
      + '    <span class="pco-bump-arrow" aria-hidden="true">&#10148;</span>'
      + '    <span class="pco-bump-box" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg></span>'
      + '    <span class="pco-bump-headtxt" id="pco-bump-head">ใช่! เพิ่มปลอกหมอน CryoGlide สำรอง</span>'
      + '  </div>'
      + '  <div class="pco-bump-body">'
      + (b.image ? '<img class="pco-bump-img" src="' + escapeHtml(b.image) + '" alt="" loading="lazy">' : '')
      + '    <span class="pco-bump-txt"><span class="pco-bump-offer">ข้อเสนอพิเศษเฉพาะออเดอร์นี้:</span> ปลอกหมอนสัมผัสเย็น CryoGlide ไว้สลับใช้ตอนซัก ให้หมอนเย็นสบายได้ทุกคืน'
      + '      <span class="pco-bump-price"></span><span class="pco-bump-qty"></span>'
      + '    </span>'
      + '  </div>'
      + '</div>';
  }

  function buildBumpHtmlV2(b) {
    return ''
      + '<div class="pco-bump pco-bump--v2" id="pco-bump" role="switch" aria-checked="false" tabindex="0" style="display:none;">'
      + '  <div class="pco-b2-label">เพิ่มในออเดอร์นี้</div>'
      + '  <div class="pco-b2-row">'
      + (b.image ? '<img class="pco-b2-img" src="' + escapeHtml(b.image) + '" alt="" loading="lazy">' : '')
      + '    <div class="pco-b2-info">'
      + '      <div class="pco-b2-title"><span class="pco-b2-tname">ปลอกหมอน CryoGlide สำรอง</span></div>'
      + '      <div class="pco-b2-sub">สัมผัสเย็น ไว้สลับใช้ตอนซัก</div>'
      + '      <div class="pco-bump-price"></div><div class="pco-bump-qty"></div>'
      + '    </div>'
      + '    <span class="pco-b2-switch" aria-hidden="true"><i></i></span>'
      + '  </div>'
      + '</div>';
  }

  function init() {
    var cfg0 = window.PCO_REAL_PDP_CONFIG || {};
    var boxed = document.querySelector(cfg0.rootSelector || '.product-boxed-section');
    if (!boxed) return; // not on a COD template, nothing to do

    // .qs-mask-banner (the "free eye mask, website customers only" banner) already sits
    // right above .qs-cards in the native markup -- left alone, so it just displays as normal.
    var qsCards = boxed.querySelector('.qs-cards');
    var form = boxed.querySelector('form');
    if (!form) return;
    var submitBtn = form.querySelector('button[type="submit"], button[name="add"]');
    if (!submitBtn) return;

    var config = window.PCO_REAL_PDP_CONFIG || {};
    var defaultLabel = submitBtn.textContent.trim();
    var reserveUrl = config.reserveUrl || '';
    var upsellPath = config.upsellPath || DEFAULT_UPSELL_PATH;
    var giftVariantId = config.giftVariantId || FREE_MASK_VARIANT_ID;
    var giftPerUnit = (config.giftPerUnit === 0 || config.giftPerUnit) ? Number(config.giftPerUnit) : 1;
    // 25 Oct 2026: pillow gift = S towels + body scrubbers instead of the eye mask (see
    // snippets/el-pillow-gifts.liquid). Opt-in per page with config.pillowGifts: true.
    var giftHelpers = window.EL_GIFT_HELPERS || null;
    var usePillowGifts = !!(config.pillowGifts && giftHelpers && window.EL_PILLOW_GIFTS);
    var addonsCfg = Array.isArray(config.addons) ? config.addons : [];
    // Order bump split test (25 Oct 2026): config.orderBump = 'v1' (ClickFunnels-style box) or
    // 'v2' (calm card with a switch); anything else = no bump (control). Set per template with the
    // pdp-hero "Order bump" setting. ?bump=v1|v2|off overrides it for previewing.
    var bumpStyle = (window.location.search.match(/[?&]bump=(v1|v2|off)\b/) || [])[1] || config.orderBump || '';
    if (bumpStyle !== 'v1' && bumpStyle !== 'v2') bumpStyle = '';
    var bumpCfg = bumpStyle ? (window.EL_PILLOW_BUMP || null) : null;
    if (bumpCfg && (!bumpCfg.variantId || bumpCfg.available === false)) bumpCfg = null;

    // Replace the theme's default ::after arrow icon on this button with a real cart icon
    // (matching the reference "2.0" buy button), via a dedicated label span so render()
    // can update the text without wiping the icon out each time.
    submitBtn.classList.add('pco-cta-btn');
    var labelSpan = document.createElement('span');
    labelSpan.className = 'pco-cta-label';
    labelSpan.textContent = defaultLabel;
    var iconImg = document.createElement('img');
    iconImg.className = 'pco-cta-icon';
    iconImg.src = CART_ICON_URL;
    iconImg.alt = '';
    iconImg.width = 21;
    iconImg.height = 20;
    // Put our label + icon in front of the theme's own children. The theme's
    // .loading__spinner MUST stay inside the button: product-form.js (the native
    // add-to-cart used by the card/other-payment path) dereferences it on submit, so
    // wiping the button's innerHTML (as this used to do) made that path throw and stall.
    function mountButtonContents() {
      Array.prototype.slice.call(submitBtn.childNodes).forEach(function (n) {
        if (n === labelSpan || n === iconImg) return;
        if (n.nodeType === 3) { submitBtn.removeChild(n); return; }
        if (n.nodeType === 1 && n.tagName === 'SPAN' && !n.classList.contains('loading__spinner') && !n.querySelector('.loading__spinner')) n.style.display = 'none';
      });
      if (labelSpan.parentNode !== submitBtn) submitBtn.insertBefore(labelSpan, submitBtn.firstChild);
      if (iconImg.parentNode !== submitBtn) submitBtn.insertBefore(iconImg, labelSpan.nextSibling);
    }
    mountButtonContents();

    var container = document.createElement('div');
    container.innerHTML = buildAddonsHtml(addonsCfg) + buildPaymentBlockHtml();
    var addonsEl = container.querySelector('#pco-real-addons');
    var block = container.querySelector('#pco-real-payment');
    var bumpEl = null, bumpOn = false, bumpRevealed = false;
    if (bumpCfg) {
      if (!document.getElementById('pco-bump-css')) {
        var bst = document.createElement('style'); bst.id = 'pco-bump-css'; bst.textContent = BUMP_CSS; document.head.appendChild(bst);
      }
      var bwrap = document.createElement('div');
      bwrap.innerHTML = bumpStyle === 'v2' ? buildBumpHtmlV2(bumpCfg) : buildBumpHtml(bumpCfg);
      bumpEl = bwrap.firstElementChild;
      block.appendChild(bumpEl); // last thing in the payment block = directly above the buy button
    }
    var anchorCfg = config.anchorSelector ? boxed.querySelector(config.anchorSelector) : null;
    var anchor = anchorCfg || ((qsCards && qsCards.parentElement) ? qsCards : null);
    if (anchor) {
      if (addonsEl) { anchor.parentElement.insertBefore(addonsEl, anchor.nextSibling); anchor = addonsEl; }
      anchor.parentElement.insertBefore(block, anchor.nextSibling);
    } else {
      if (addonsEl) boxed.insertBefore(addonsEl, boxed.firstChild);
      boxed.insertBefore(block, addonsEl ? addonsEl.nextSibling : boxed.firstChild);
    }
    // When we render our own cross-sell rows, the theme's async "complementary
    // products" block (adds to the REAL cart, different widget) must never show --
    // the seat tier script calls jQuery.slideDown() on it after a tier click, so a
    // CSS rule alone is not enough: remove it from the page outright.
    if (addonsCfg.length) {
      Array.prototype.forEach.call(document.querySelectorAll('.product-complementary-section, product-recommendations.complementary-products'), function (el) {
        if (el && el.parentNode) el.parentNode.removeChild(el);
      });
    }
    // Add-on rows: tap anywhere on the row to toggle; the big +/- stepper sets the
    // quantity (and "+" on an unticked row ticks it). State lives here, not in the DOM.
    var addonState = addonsCfg.map(function () { return { checked: false, qty: 1 }; });
    if (addonsEl) {
      Array.prototype.forEach.call(addonsEl.querySelectorAll('.pco-real-addon'), function (row) {
        var i = parseInt(row.getAttribute('data-index'), 10);
        var st = addonState[i];
        var cfg = addonsCfg[i] || {};
        var max = Math.max(1, parseInt(cfg.maxQty, 10) || 4);
        var check = row.querySelector('.pco-real-addon-check');
        var qval = row.querySelector('.pco-real-addon-qval');
        function paint() {
          row.classList.toggle('sel', st.checked);
          check.checked = st.checked;
          qval.textContent = String(st.qty);
          updateTotal();
        }
        row.addEventListener('click', function (e) {
          if (e.target.closest('.pco-real-addon-qty')) return;
          // The label (box + hidden checkbox) toggles natively; ignore the bubbled click so the
          // row handler does not toggle it a second time (which made the box itself look dead).
          if (e.target.closest('.pco-real-addon-checkwrap')) return;
          st.checked = !st.checked;
          paint();
        });
        check.addEventListener('click', function (e) { e.stopPropagation(); st.checked = check.checked; paint(); });
        row.querySelector('.pco-real-addon-minus').addEventListener('click', function (e) {
          e.stopPropagation();
          if (st.qty > 1) st.qty -= 1; else st.checked = false;
          paint();
        });
        row.querySelector('.pco-real-addon-plus').addEventListener('click', function (e) {
          e.stopPropagation();
          // "+" on an unticked row ticks it AND steps the quantity in one tap (Luka, 30 Sep)
          if (!st.checked) st.checked = true;
          if (st.qty < max) st.qty += 1;
          paint();
        });
        paint();
      });
    }
    function selectedAddons() {
      var out = [];
      addonsCfg.forEach(function (a, i) {
        if (!addonState[i] || !addonState[i].checked || !a || !a.variantId) return;
        out.push({
          variantId: String(a.variantId),
          qty: addonState[i].qty,
          productTitle: a.title || '',
          image: a.image || '',
          price: a.price, compare: a.compare
        });
      });
      if (bumpCfg && bumpOn && bumpVisible()) {
        out.push({
          variantId: String(bumpCfg.variantId),
          qty: bumpQty(),
          productTitle: bumpCfg.title || '',
          image: bumpCfg.image || '',
          price: bumpCfg.price, compare: bumpCfg.compare,
          bump: true
        });
      }
      return out;
    }
    // Pillows in the selected bundle = pillowcases in the bump (1 per pillow).
    function bumpQty() {
      var card = document.querySelector('.qs-cards .qs-card.sel');
      var q = card ? parseInt(card.getAttribute('data-qty'), 10) : 0;
      if (!q) { try { q = parseInt(new FormData(form).get('quantity'), 10); } catch (e) { q = 0; } }
      return q > 0 ? q : 1;
    }
    function bumpVisible() { return !!(bumpEl && bumpEl.style.display !== 'none'); }

    var methodBtns = Array.prototype.slice.call(block.querySelectorAll('.pco-real-method-btn'));
    var cardLink = block.querySelector('#pco-real-card-link');
    var innerForm = block.querySelector('#pco-real-form');
    var msgEl = block.querySelector('#pco-real-msg');
    var noteEl = block.querySelector('#pco-real-note');
    var chosen = null;

    // Matches the note already shown on the isolated 2.0 test's Bank Transfer option:
    // tells the customer up front (before they submit) that they'll get the bank details
    // and a LINE link for the payment slip right after ordering. No equivalent note for COD.
    var BANK_NOTE_TEXT = 'หลังจากสั่งซื้อ คุณจะได้รับรายละเอียดบัญชีธนาคารและลิงก์ LINE สำหรับส่งสลิปการชำระเงินทันที';

    function updateNote() {
      if (!noteEl) return;
      if (chosen === 'bank') {
        noteEl.textContent = BANK_NOTE_TEXT;
        noteEl.style.display = '';
      } else {
        noteEl.textContent = '';
        noteEl.style.display = 'none';
      }
    }

    function requiredFields() {
      return [
        block.querySelector('#pco-real-name'),
        block.querySelector('#pco-real-phone'),
        block.querySelector('#pco-real-address'),
        block.querySelector('#pco-real-zip')
      ];
    }

    function isCustomMethod() { return chosen === 'cod' || chosen === 'bank'; }

    function valid() {
      if (!isCustomMethod()) return true;
      return requiredFields().every(function (f) { return f && f.value.trim().length > 0; });
    }

    var wantedLabel = '';
    var wantedDisabled = true;

    // The theme's quantity-tier widget rewrites this button's contents (text back to
    // "หยิบใส่ตะกร้า", our icon gone) on load and every time a tier is clicked, which
    // wiped our label. applyButton() restores exactly what render() decided, and the
    // MutationObserver below re-applies it whenever anything else touches the button.
    var applying = false;
    function applyButton() {
      applying = true;
      // If the widget replaced the whole button element, move over to the new one.
      var liveBtn = form.querySelector('button[type="submit"], button[name="add"]');
      if (liveBtn && liveBtn !== submitBtn) {
        submitBtn = liveBtn;
        submitBtn.classList.add('pco-cta-btn');
        observeButton();
      }
      if (labelSpan.parentNode !== submitBtn || iconImg.parentNode !== submitBtn) mountButtonContents();
      if (labelSpan.textContent !== wantedLabel) labelSpan.textContent = wantedLabel;
      submitBtn.classList.toggle('pco-real-disabled', wantedDisabled);
      applying = false;
    }

    // Order total line (7 Oct 2026): shown only once COD or Bank Transfer is picked, so the
    // customer sees what they will pay before confirming. Reads the selected quantity tier
    // card (label, struck price, price) plus any ticked add-ons.
    var totalRow = block.querySelector('#pco-real-total');
    var totalLabel = block.querySelector('#pco-real-total-label');
    var totalValue = block.querySelector('#pco-real-total-value');
    function bahtToNumber(str) { var n = parseFloat(String(str || '').replace(/[^0-9.]/g, '')); return isNaN(n) ? 0 : n; }
    function updateTotal() {
      if (!totalRow) return;
      try {
        var card = (chosen === 'cod' || chosen === 'bank') ? document.querySelector('.qs-cards .qs-card.sel') : null;
        var priceEl = card && card.querySelector('.qs-price');
        var total = priceEl ? bahtToNumber(priceEl.textContent) : 0;
        if (!card || !total) { totalRow.style.display = 'none'; return; }
        var strikeEl = card.querySelector('.qs-strike');
        var compare = strikeEl ? bahtToNumber(strikeEl.textContent) : 0;
        if (compare < total) compare = total;
        var qtyEl = card.querySelector('.qs-qty');
        var qtyText = qtyEl ? qtyEl.textContent.replace(/\s+/g, ' ').trim() : '';
        // Label: small "ยอดรวม" caption, then "<product name> - 2x" (falls back to the tier text).
        var nameEl = document.querySelector('.hero_heading') || document.querySelector('h1');
        var prodName = String(config.productTitle || (nameEl ? nameEl.textContent : '') || '').replace(/\s+/g, ' ').trim().replace(/\s+\d+\.\d+$/, '');
        var qtyNum = parseInt(card.getAttribute('data-qty'), 10) || parseInt((qtyText.match(/\d+/) || [])[0], 10) || 0;
        var unitM = qtyText.match(/\d+\s*(\S+)\s*$/);
        var detail = (prodName && qtyNum) ? (prodName + ' - ' + qtyNum + (unitM ? ' ' + unitM[1] : 'x')) : qtyText;
        // With add-ons ticked: "2 เบาะรองนั่ง + 2 เบาะรองหลัง" (Thai product words only).
        function thaiShort(t) { return String(t || '').replace(/[A-Za-z0-9().\-]+/g, ' ').replace(/\s+/g, ' ').trim(); }
        var mainShort = thaiShort(config.productTitle || (nameEl ? nameEl.textContent : ''));
        var addonParts = [];
        selectedAddons().forEach(function (a) {
          var q = a.qty || 1, pr = Number(a.price) || 0, cp = Number(a.compare) || 0;
          total += pr * q; compare += (cp > pr ? cp : pr) * q;
          addonParts.push(q + ' ' + (thaiShort(a.productTitle) || 'สินค้าเพิ่ม'));
        });
        if (addonParts.length && qtyNum && mainShort) detail = qtyNum + ' ' + mainShort + ' + ' + addonParts.join(' + ');
        else if (addonParts.length) detail += ' + ' + addonParts.join(' + ');
        totalLabel.innerHTML = '<span style="display:block;font-weight:400;font-size:12px;color:#666;margin-bottom:1px;">ยอดรวม</span><span></span>';
        totalLabel.lastChild.textContent = detail;
        totalValue.innerHTML = (compare > total ? '<s style="color:#888;font-size:14px;font-weight:400;margin-right:8px;">' + formatBaht(compare) + '</s>' : '')
          + '<b style="color:#1A256C;font-size:20px;font-weight:800;">' + formatBaht(total) + '</b>';
        totalRow.style.display = 'flex';
      } catch (e) { totalRow.style.display = 'none'; }
    }
    if (addonsEl) addonsEl.addEventListener('click', function () { setTimeout(updateTotal, 0); });

    function updateBump() {
      if (!bumpEl) return;
      var show = false;
      if (chosen === 'card') show = true;
      else if (isCustomMethod()) {
        // COD / bank: only once the delivery form is filled in, then it stays put.
        if (valid()) bumpRevealed = true;
        show = bumpRevealed;
      }
      if (show && bumpEl.style.display === 'none') {
        bumpEl.style.display = '';
        bumpEl.style.animation = 'none'; void bumpEl.offsetWidth; bumpEl.style.animation = '';
      } else if (!show) bumpEl.style.display = 'none';
      // Show the TOTAL the customer adds (all pieces), the per-piece price only as small print,
      // so nobody expects to pay ฿490 for 2 and refuses the parcel (Luka, 25 Oct).
      var q = bumpQty();
      var unit = Number(bumpCfg.price) || 0, unitCmp = Number(bumpCfg.compare) || 0;
      var headEl = bumpEl.querySelector('#pco-bump-head');
      if (headEl) headEl.textContent = 'ใช่! เพิ่มปลอกหมอน CryoGlide ' + q + ' ใบ';
      var tEl = bumpEl.querySelector('.pco-b2-tname');
      if (tEl) tEl.textContent = 'ปลอกหมอน CryoGlide สำรอง ' + q + ' ใบ';
      var pEl = bumpEl.querySelector('.pco-bump-price');
      if (pEl) pEl.innerHTML = (bumpStyle === 'v2' ? '+' : 'เพิ่ม ') + '<b>' + formatBaht(unit * q) + '</b>' + (unitCmp > unit ? ' <s>' + formatBaht(unitCmp * q) + '</s>' : '') + ' สำหรับ ' + q + ' ใบ';
      var qEl = bumpEl.querySelector('.pco-bump-qty');
      if (qEl) qEl.textContent = q > 1 ? '(ราคาใบละ ' + formatBaht(unit) + ')' : '';
      bumpEl.classList.toggle('on', bumpOn);
      bumpEl.setAttribute('aria-checked', bumpOn ? 'true' : 'false');
    }
    if (bumpEl) {
      var toggleBump = function () { bumpOn = !bumpOn; updateBump(); updateTotal(); };
      bumpEl.addEventListener('click', toggleBump);
      bumpEl.addEventListener('keydown', function (e) { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggleBump(); } });
    }

    function render() {
      updateBump();
      updateTotal();
      if (!chosen) {
        wantedLabel = 'เลือกวิธีการชำระเงิน';
        wantedDisabled = true;
      } else if (chosen === 'card') {
        wantedLabel = defaultLabel;
        wantedDisabled = false;
      } else {
        wantedLabel = 'ยืนยันการสั่งซื้อ';
        wantedDisabled = !valid();
      }
      applyButton();
    }

    var btnObserver = null;
    function observeButton() {
      if (!window.MutationObserver) return;
      if (btnObserver) btnObserver.disconnect();
      btnObserver = new MutationObserver(function () {
        if (applying) return;
        if (labelSpan.parentNode !== submitBtn || labelSpan.textContent !== wantedLabel
          || submitBtn.classList.contains('pco-real-disabled') !== wantedDisabled) {
          applyButton();
        }
      });
      btnObserver.observe(submitBtn, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    }
    observeButton();
    // Also watch the form itself in case the button element gets swapped out entirely.
    if (window.MutationObserver) {
      new MutationObserver(function () {
        if (applying) return;
        var liveBtn = form.querySelector('button[type="submit"], button[name="add"]');
        if (liveBtn && liveBtn !== submitBtn) applyButton();
      }).observe(form, { childList: true, subtree: true });
    }
    // Belt and braces for scripts that swap the whole button rather than editing it.
    document.addEventListener('click', function (e) {
      if (e.target && e.target.closest && e.target.closest('.omg-option-item, .qs-card, .qs-cards')) setTimeout(render, 0);
    }, true);

    methodBtns.forEach(function (b) {
      b.addEventListener('click', function () {
        methodBtns.forEach(function (x) { x.classList.remove('sel'); });
        b.classList.add('sel');
        chosen = b.getAttribute('data-method');
        if (innerForm) innerForm.style.display = '';
        updateNote();
        render();
      });
    });

    if (cardLink) {
      cardLink.addEventListener('click', function () {
        methodBtns.forEach(function (x) { x.classList.remove('sel'); });
        chosen = 'card';
        if (innerForm) innerForm.style.display = 'none';
        updateNote();
        render();
      });
    }

    requiredFields().forEach(function (f) { if (f) f.addEventListener('input', render); });

    render();

    function reserveOrder(session, variantId, quantity, attrs) {
      if (!reserveUrl) return;
      try {
        // Same line list the confirm page would send if the customer declined the
        // upsell: base, any checked add-ons, then the free gift (1 per base unit).
        var lines = [{ variantId: variantId, quantity: quantity, role: 'base' }];
        (session.addons || []).forEach(function (a) {
          lines.push({ variantId: a.variantId, quantity: a.qty, role: 'addon' });
        });
        if (session.gift && session.gift.plan && giftHelpers) {
          giftHelpers.lines(session.gift.plan, quantity).forEach(function (g) {
            lines.push({ variantId: g.variantId, quantity: g.quantity, role: 'gift' });
          });
        } else {
          var giftQty = quantity * giftPerUnit;
          if (giftQty > 0) lines.push({ variantId: giftVariantId, quantity: giftQty, role: 'gift' });
        }
        fetch(reserveUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          keepalive: true,
          body: JSON.stringify({
            cartToken: session.token,
            lineItems: lines,
            customer: session.customer,
            cartAttributes: attrs
          })
        }).catch(function () { /* best-effort */ });
      } catch (e) { /* noop */ }
    }

    // Card / other payment with the bump ticked: add the pillows AND the pillowcases to the real
    // cart in ONE request, then open the cart like the theme does. (Two separate adds raced with
    // the cart app's reward clean-up, which removed the pillowcase line.) Caught on the button
    // click and on submit, both in the capture phase, so the theme's own handler never runs.
    var bumpCartBusy = false;
    function bumpCardWanted() { return chosen === 'card' && bumpCfg && bumpOn && bumpVisible(); }
    function addWithBump() {
      if (bumpCartBusy) return;
      bumpCartBusy = true;
      var fd = new FormData(form);
      var main = { id: Number(fd.get('id')), quantity: parseInt(fd.get('quantity'), 10) || 1 };
      var props = {};
      fd.forEach(function (v, k) { var m = String(k).match(/^properties\[(.+)\]$/); if (m) props[m[1]] = v; });
      if (Object.keys(props).length) main.properties = props;
      submitBtn.classList.add('pco-real-disabled');
      fetch(root() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [main, { id: Number(bumpCfg.variantId), quantity: bumpQty() }] })
      }).then(function (r) { return r.json().then(function (j) { return { ok: r.ok, j: j }; }); })
        .then(function (res) {
          if (!res.ok) return res;
          return fetch(root() + 'cart/update.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ attributes: { order_bump: bumpStyle, order_bump_taken: 'yes' } })
          }).catch(function () {}).then(function () { return res; });
        })
        .then(function (res) {
          if (!res.ok) throw new Error((res.j && (res.j.description || res.j.message)) || 'Could not add to cart');
          if ((form.getAttribute('data-after') || 'cart') === 'checkout') { window.location.href = root() + 'checkout'; return; }
          if (typeof window.upcartRefreshCart === 'function' && typeof window.upcartOpenCart === 'function') {
            try { window.upcartRefreshCart(); window.upcartOpenCart(); } catch (e2) { window.location.href = root() + 'cart'; }
          } else { window.location.href = root() + 'cart'; }
        })
        .catch(function (err) { if (msgEl) { msgEl.textContent = (err && err.message) || 'Something went wrong'; msgEl.hidden = false; } })
        .then(function () { submitBtn.classList.remove('pco-real-disabled'); bumpCartBusy = false; });
    }
    document.addEventListener('click', function (e) {
      var btn = e.target && e.target.closest && e.target.closest('button');
      if (!btn || btn !== submitBtn || !bumpCardWanted()) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      addWithBump();
    }, true);
    document.addEventListener('submit', function (e) {
      if (e.target !== form || !bumpCardWanted()) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      addWithBump();
    }, true);

    document.addEventListener('submit', function (e) {
      if (e.target !== form) return;
      if (!isCustomMethod()) return; // card / no selection: leave the native add-to-cart handler untouched
      e.preventDefault();
      e.stopImmediatePropagation();
      if (!valid()) return;

      var paymentMethod = PAYMENT_METHOD_LABELS[chosen] || PAYMENT_METHOD_LABELS.cod;
      var name = block.querySelector('#pco-real-name').value.trim();
      var phone = formatThaiPhone(block.querySelector('#pco-real-phone').value.trim());
      var email = block.querySelector('#pco-real-email').value.trim();
      // Email is optional. A malformed one must never block or lose the order, so if it
      // doesn't look like an address, just don't send it (Shopify would reject the order).
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) email = '';
      var address = block.querySelector('#pco-real-address').value.trim();
      var zip = block.querySelector('#pco-real-zip').value.trim();

      var fd = new FormData(form);
      var variantId = fd.get('id');
      var quantity = parseInt(fd.get('quantity'), 10) || 1;

      submitBtn.classList.add('pco-real-disabled');

      fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .catch(function () { return {}; })
        .then(function (cart) {
          var session = {
            token: pcoGenToken(),
            variantId: variantId,
            quantity: quantity,
            productTitle: config.productTitle || '',
            price: '',
            comparePrice: '',
            image: config.productImage || '',
            paymentMethod: paymentMethod,
            customer: { fullName: name, phone: phone, email: email, address: address, zip: zip },
            cartAttributes: (cart && cart.attributes) || {},
            addons: selectedAddons(),
            gift: usePillowGifts
              ? { variantId: '', perUnit: 1, plan: giftHelpers.pickPlan(window.EL_PILLOW_GIFTS) }
              : { variantId: giftVariantId, perUnit: giftPerUnit },
            upsell: null
          };
          // Upsell-page split test (4 Oct 2026): A = red decline link + "adds ฿X" line,
          // B = red decline link only. Stamped on the order as custom attribute upsell_variant
          // (both the reserve call and the confirm page copy session.cartAttributes).
          session.upsellVariant = (Math.random() < 0.5) ? 'A' : 'B';
          session.cartAttributes.upsell_variant = session.upsellVariant;
          // Bank-transfer upsell test (5 Oct 2026): bank customers who see the upsell mostly do not
          // pay, so 50% of bank orders skip the upsell pages and go straight to the confirmation
          // page. Stamped on the order as custom attribute bank_upsell = skip | show.
          session.bankUpsell = '';
          // 9 Oct 2026 21:2x: COD upsell switched back ON (Thailand Post data 5-7 Oct: upsell takers
          // refuse more but collect ~20% more per order). Bank transfer still always skips the upsell.
          if (chosen === 'bank') {
            session.bankUpsell = 'skip'; // 6 Oct 2026: test ended, bank transfer always skips the upsell (?bu=show still previews it)
            var buForce = (window.location.search.match(/[?&]bu=(skip|show)\b/) || [])[1]; // preview/testing switch
            if (buForce) session.bankUpsell = buForce;
            session.cartAttributes.bank_upsell = session.bankUpsell;
            if (session.bankUpsell === 'skip') { session.upsellVariant = ''; delete session.cartAttributes.upsell_variant; }
          }
          // 24 Oct 2026: page-level extra attributes (e.g. cod_source: 'pillow-cod-listicle' on the pillow
          // listicle buy box). The worker turns cod_source into an order tag. Not set on the product page.
          if (bumpCfg) {
            session.cartAttributes.order_bump = bumpStyle;
            session.cartAttributes.order_bump_taken = (bumpOn && bumpVisible()) ? 'yes' : 'no';
          }
          var extra = config.extraAttributes || {};
          Object.keys(extra).forEach(function (k) { session.cartAttributes[k] = extra[k]; });
          pcoSaveSession(session);

          var reserveAttrs = {};
          Object.keys(session.cartAttributes).forEach(function (k) { reserveAttrs[k] = session.cartAttributes[k]; });
          reserveAttrs['Payment Method'] = paymentMethod;
          reserveAttrs['Full Name'] = name;
          reserveAttrs['Phone'] = phone;
          reserveAttrs['Email'] = email;
          reserveAttrs['Full Address'] = address;
          reserveAttrs['Zip Code'] = zip;
          reserveOrder(session, variantId, quantity, reserveAttrs);

          window.location.href = root() + (session.bankUpsell === 'skip' ? 'pages/cod-order-confirmation' : upsellPath);
        })
        .catch(function (err) {
          submitBtn.classList.remove('pco-real-disabled');
          if (msgEl) { msgEl.textContent = (err && err.message) || 'Something went wrong'; msgEl.hidden = false; }
        });
    }, true);
  }

  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }
})();
